import { Quaternion } from "@babylonjs/core/Maths/math.vector";
import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { MoveOpening, MoveWallOpening, OpenWallOpening } from "./tools";
import { AbstractInteractors } from "./AbstractInteractors";
import { EditWallOpening } from './../cmd/EditWallOpening';
import { Util } from "../utils/Util";
import { InteractorType } from '@egr/wcf/modules/core/tool';
import { PI_DIV2, degToRad } from "@egr/wcf/modules/utils/math";
import { getRotationQuaternion } from "@egr/wcf/modules/core/utils/MeshUtils";
import { perpendicularVector2, VECTOR3_X, VECTOR3_Y, VECTOR3_Z } from "@egr/wcf/modules/core/utils/MathUtils";
export class WallOpeningInteractors extends AbstractInteractors {
    constructor(pRoomManager, pOpening) {
        super(pRoomManager);
        this.mOpening = pOpening;
        this.mDistInteractors = [null, null, null, null, null];
        this.mWidthInteractor = null;
        this.mView = null;
        this.mShowInteractors = false;
    }
    showInteractors(pShow) {
        this.mShowInteractors = pShow;
        this.clear();
        if (!pShow || this.mApp.model.selection.length !== 1)
            return;
        this.mInteractorManager.enabled = true;
        let tDir = this.mOpening.getEdge(1, false).subtract(this.mOpening.getEdge(0, false)).normalize();
        let tDirP = perpendicularVector2(tDir);
        let t3dRotation = getRotationQuaternion(this.mOpening.node).clone();
        t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        let twm = this.mOpening.node.getWorldMatrix().getRotationMatrix();
        //move
        let tInteractor = this.createInteractor("move-interactor", this.mOpening.node.position, null, 0, t3dRotation, undefined, true);
        tInteractor.onTriggerAction = () => this.startMoveOpening();
        //width right
        let toffsetDirR = new Vector3(1, 0, 0).normalize();
        toffsetDirR = Vector3.TransformCoordinates(toffsetDirR, twm);
        let tposR = Util.to3d(this.mOpening.endPoint);
        //width left
        let toffsetDirL = new Vector3(-1, 0, 0).normalize();
        toffsetDirL = Vector3.TransformCoordinates(toffsetDirL, twm);
        let tposL = Util.to3d(this.mOpening.startPoint);
        let trotationL = t3dRotation.multiply(Quaternion.RotationAxis(VECTOR3_Y, Math.PI));
        let thandleRot = t3dRotation.multiply(Quaternion.RotationAxis(VECTOR3_Z, degToRad(-this.mOpening.OpeningParams.openingAngle)));
        if (!this.mRoomManager.isInteracting) {
            let tInteractorR = this.createInteractor("dot-interactor", tposR, null, 0, t3dRotation);
            tInteractorR.onTriggerAction = () => this.startScaleOpening(tposL, tposR, MoveOpening.Mode.ScaleEnd);
            let tInteractorL = this.createInteractor("dot-interactor", tposL, null, 0, trotationL);
            tInteractorL.onTriggerAction = () => this.startScaleOpening(tposL, tposR, MoveOpening.Mode.ScaleStart);
            /*
            // open intertor (only single doors yet)
            if (this.mOpening.type == "Door" || this.mOpening.type == "GlassDoor" ||
                this.mOpening.type == "GlassInlayDoor") {
                let tHP = this.mOpening.handlePosition;
                let tHingeP = this.mOpening.hingePosition;
                let tInteractorAngle = this.createInteractor("open-interactor", tHP, null, 0, thandleRot);
                tInteractorAngle.onTriggerAction = () => this.changeOpeningAngle(tHingeP, tHP);
            }
            */
            //mirror
            if ((this.mOpening.geometry.canMirrorV || this.mOpening.geometry.canMirrorH) && this.mRoomManager.MirrorInteractorVisible) {
                let tPos = Vector2.Center(this.mOpening.getEdge(1, false), this.mOpening.getEdge(0, false));
                let tInteractorMirr = this.createInteractor("mirror-interactor", Util.to3d(tPos), Util.to3d(tDirP).normalize(), 22, t3dRotation, true);
                tInteractorMirr.type = InteractorType.Button;
                tInteractorMirr.onTriggerAction = () => this.mirrorOpening();
                tInteractorMirr.updateViewPosition();
            }
        }
        //distance text interactors
        this._updateDistInteractors();
        //opening width interactor
        this._showOpeningWidthInteractors();
    }
    clear() {
        this.mInteractorManager.clear();
        this.mInteractorManager.enabled = false;
        this.mDistInteractors = [null, null, null, null];
        this.mWidthInteractor = null;
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    _updateDistInteractors() {
        this.mInteractorManager.enabled = true;
        let t3dRotation = getRotationQuaternion(this.mOpening.node).clone();
        t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        if (this.mOpening.wall != null) {
            let tWall = this.mOpening.wall;
            let tDir = tWall.endPoint.subtract(tWall.startPoint).normalize();
            let tDirP = perpendicularVector2(tDir);
            for (let i = 0; i < 4; i++) {
                let tDist = this.mOpening._getDistanceEdge(i);
                let tPos = this.getDistTextPos(i, tDist);
                let tOffset = i >= 2 ? -15 : 15;
                if (Math.abs(tDist) > 0.01) {
                    let tInteractor = this.createTextInteractor(Util.to3d(tPos), this.lengthToString(tDist) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir).normalize(), Util.to3d(tDirP).normalize(), tOffset, t3dRotation, false, this.mDistInteractors[i]);
                    tInteractor.onTriggerAction = () => this.showInputField(WallOpeningInteractors.INPUT_DIST, tDist, tInteractor);
                    tInteractor._edge = i;
                    tInteractor.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractor.element.firstChild);
                    tInteractor.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractor.element.firstChild);
                    this.mDistInteractors[i] = tInteractor;
                }
            }
        }
    }
    /** @internal */
    _showOpeningWidthInteractors() {
        this.mInteractorManager.enabled = true;
        let t3dRotation = getRotationQuaternion(this.mOpening.node).clone();
        t3dRotation.multiplyInPlace(Quaternion.RotationAxis(VECTOR3_X, PI_DIV2));
        if (this.mOpening.wall != null) {
            let tWall = this.mOpening.wall;
            let tDir = tWall.endPoint.subtract(tWall.startPoint).normalize();
            let tDirP = perpendicularVector2(tDir);
            let tWidth = this.mOpening.width;
            let tPos = Vector2.Center(this.mOpening.getEdge(1, false), this.mOpening.getEdge(0, false));
            let toffset = this.mRoomManager.MirrorInteractorVisible ? 45 : 15;
            let tInteractor = this.createTextInteractor(Util.to3d(tPos), this.lengthToString(tWidth) + Util.getLengthUnitString(this.mRoomManager.app.i18n.lengthUnit), Util.to3d(tDir).normalize(), Util.to3d(tDirP).normalize(), toffset, t3dRotation, false, this.mDistInteractors[4]);
            tInteractor.onTriggerAction = () => this.showInputField(WallOpeningInteractors.INPUT_WIDTH, tWidth, tInteractor);
            tInteractor.element.onmouseenter = () => this._mouseEnter(this.mRoomManager, tInteractor.element.firstChild);
            tInteractor.element.onmouseleave = () => this._mouseLeave(this.mRoomManager, tInteractor.element.firstChild);
            this.mDistInteractors[4] = tInteractor;
        }
    }
    //// protected /////////////////////////////////////////////////////////////////////////////
    handleInputField(pId, pOldLength, pNewLength, pInteractor) {
        if (pId === WallOpeningInteractors.INPUT_DIST)
            this.setDistanceEdge(pInteractor._edge, pOldLength, pNewLength);
        if (pId === WallOpeningInteractors.INPUT_WIDTH) {
            let tCmd = new EditWallOpening(this.mRoomManager, this.mOpening);
            this.mOpening.setWidth(pNewLength);
            this.mApp.commands.executeCommand(tCmd);
        }
    }
    _mouseEnter(pRoomManager, pSpanElement) {
        let tStyle = pSpanElement.style;
        tStyle.color = pRoomManager.app.theme.mainColorBright;
    }
    _mouseLeave(pRoomManager, pSpanElement) {
        let tStyle = pSpanElement.style;
        tStyle.color = "#4f4f4f";
    }
    getDistTextPos(pDistIterator, pDistance) {
        let tV1 = this.mOpening.getEdge(pDistIterator, false);
        let tV2;
        switch (pDistIterator) {
            case 0:
                tV2 = this.mOpening.getEdge(1, false);
                break;
            case 1:
                tV2 = this.mOpening.getEdge(0, false);
                break;
            case 2:
                tV2 = this.mOpening.getEdge(3, false);
                break;
            default: //3
                tV2 = this.mOpening.getEdge(2, false);
                break;
        }
        let tVDir = tV1.subtract(tV2).normalize();
        let tV = tV1.add(tVDir.scaleInPlace(pDistance / 2));
        return (tV);
    }
    startMoveOpening() {
        let tTool = new MoveWallOpening(this.mRoomManager);
        tTool.initialize(this.mOpening);
        this.mApp.tools.startTool(tTool);
    }
    startScaleOpening(pPosL, pPosR, pMode) {
        let tTool = new MoveWallOpening(this.mRoomManager);
        tTool.initialize(this.mOpening, pMode, pPosL, pPosR);
        this.mApp.tools.startTool(tTool);
    }
    changeOpeningAngle(pHingePos, pHandlePos) {
        let tView = this.mView;
        if (tView == null)
            tView = this.mRoomManager.hoverView;
        if (tView == null)
            return;
        let tTool = new OpenWallOpening(this.mRoomManager);
        tTool.initialize(this.mOpening, pHingePos, pHandlePos);
        this.mApp.tools.startTool(tTool);
    }
    mirrorOpening() {
        let tCmd = new EditWallOpening(this.mRoomManager, this.mOpening);
        if (this.mOpening.geometry.canMirrorV && this.mOpening.geometry.canMirrorH) {
            let tH = this.mOpening.mirrorHorizontal;
            let tV = this.mOpening.mirrorVertical;
            if (tH && tV)
                this.mOpening.setMirrorVertical(false);
            else if (tH)
                this.mOpening.setMirrorHorizontal(false);
            else if (tV)
                this.mOpening.setMirrorHorizontal(true);
            else
                this.mOpening.setMirrorVertical(true);
        }
        else if (this.mOpening.geometry.canMirrorV) {
            this.mOpening.setMirrorVertical(!this.mOpening.mirrorVertical);
        }
        else if (this.mOpening.geometry.canMirrorH) {
            this.mOpening.setMirrorHorizontal(!this.mOpening.mirrorHorizontal);
        }
        this.mApp.commands.executeCommand(tCmd);
    }
    setDistanceEdge(pEdge, pOldDist, pDist) {
        let tDiff = pDist - pOldDist;
        let tNewPos;
        let tWall = this.mOpening.wall;
        if (tWall == null)
            return;
        if (pEdge === 0 || pEdge === 3) {
            let tV1 = this.mOpening.position.subtract(tWall.startPoint);
            let tOldLength = tV1.length();
            tV1 = tWall.endPoint.subtract(tWall.startPoint).normalize();
            tV1.scaleInPlace(tOldLength + tDiff);
            tNewPos = tV1.add(tWall.startPoint);
        }
        else {
            let tV1 = this.mOpening.position.subtract(tWall.endPoint);
            let tOldLength = tV1.length();
            tV1 = tWall.startPoint.subtract(tWall.endPoint).normalize();
            tV1.scaleInPlace(tOldLength + tDiff);
            tNewPos = tV1.add(tWall.endPoint);
        }
        let tCmd = new EditWallOpening(this.mRoomManager, this.mOpening);
        this.mOpening.setPosition(tNewPos);
        this.mApp.commands.executeCommand(tCmd);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
WallOpeningInteractors.INPUT_DIST = "WallOpening:distance_edge";
WallOpeningInteractors.INPUT_WIDTH = "WallOpening:width";
